import { fetcher } from '#lib/backendFetcher';

function getBoundsCenter(bounds) {
	return {
		lat: (bounds.north + bounds.south) / 2,
		lng: (bounds.east + bounds.west) / 2,
	};
}

function getBoundsRadius(bounds) {
	const center = getBoundsCenter(bounds);

	// Calculate distance from center to corner
	const lat1 = (center.lat * Math.PI) / 180;
	const lat2 = (bounds.north * Math.PI) / 180;
	const lng1 = (center.lng * Math.PI) / 180;
	const lng2 = (bounds.east * Math.PI) / 180;

	const dLat = lat2 - lat1;
	const dLng = lng2 - lng1;

	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	const radius = 6371000 * c; // Earth radius in meters

	// Cap at 50000 meters (API maximum)
	return Math.min(Math.round(radius), 50000);
}

/**
 * Searches for places by category within the visible map bounds.
 * @param {string|string[]} category - Single category or array of categories
 * @param {Object} bounds - Visible map bounds
 * @param {number} bounds.north - Northern latitude
 * @param {number} bounds.south - Southern latitude
 * @param {number} bounds.east - Eastern longitude
 * @param {number} bounds.west - Western longitude
 * @returns {Promise<Object>}
 */
export async function fetchPlacesByCategory(category, bounds) {
	const center = getBoundsCenter(bounds);
	const radius = getBoundsRadius(bounds);

	// Category can now be a string or an array of types
	return fetcher('/api/v1/search/maps/places/search', {
		method: 'POST',
		body: {
			category: Array.isArray(category) ? category : [category],
			lat: center.lat,
			lng: center.lng,
			radius,
			language: 'de',
		},
	});
}

/**
 * Searches for places by a free-text search term within the visible map bounds.
 * @param {string} query - Free-text search term
 * @param {Object} bounds - Visible map bounds
 * @param {number} bounds.north - Northern latitude
 * @param {number} bounds.south - Southern latitude
 * @param {number} bounds.east - Eastern longitude
 * @param {number} bounds.west - Western longitude
 * @returns {Promise<Object>}
 */
export async function fetchPlacesByName(query, bounds) {
	const center = getBoundsCenter(bounds);
	const radius = getBoundsRadius(bounds);

	return fetcher('/api/v1/search/maps/places/search-by-name', {
		method: 'POST',
		body: {
			searchTerm: query,
			lat: center.lat,
			lng: center.lng,
			radius,
			language: 'de',
		},
	});
}

/**
 * Fetches detailed information for a single place by its Place ID.
 * @param {string} placeId - Google Places ID
 * @returns {Promise<Object>}
 */
export async function fetchPlaceDetails(placeId) {
	return fetcher(`/api/v1/search/maps/places/${placeId}?language=de`);
}
